// VerificationGateModal — Phase 2E.1 Step 3 · the $500 verification gate.
// Spec: Playbook § 5.6 Decision 6 sub-point 4 + BUILD_GUIDE § 5.9 Step 3.
//
// Two modes (Phase 2E.3 extended this with the buyer-side gate):
//
//   mode='seller' (default, original behavior)
//     Triggered by CollectionModal when a seller tries to publish a listing
//     with sale_price > 500 and identity_verified = false. The listing has
//     already been upserted with is_paused = true; this modal either:
//       (a) verifies → flips is_paused = false → onPublished()
//       (b) lets them save the draft for later (close + leave is_paused = true)
//
//   mode='buyer' (Phase 2E.3)
//     Triggered by ListingDetailModal when an unverified buyer taps Open
//     Escrow on a ≥$500 listing and the marketplace-create-escrow-deal
//     edge function returns 412 verification_required. No draft exists;
//     verification simply unlocks the next startEscrow attempt. onVerified()
//     fires after polling confirms; the caller re-invokes startEscrow.
//
// Privacy + race notes (both modes):
//   - Persona's onComplete fires on the client before our webhook flips
//     identity_verified server-side. We poll for the flip, then run the
//     mode-specific side-effect (seller: unpause listing; buyer: nothing).
//     If the user closes the modal mid-poll, side effects still run (no
//     React-state dependency), but UI feedback is gated on mounted state.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Persona from 'persona';
import { supabase } from '../supabaseClient';

const PERSONA_TEMPLATE_ID = process.env.REACT_APP_PERSONA_TEMPLATE_ID;
const PERSONA_ENVIRONMENT_ID = process.env.REACT_APP_PERSONA_ENVIRONMENT_ID;

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000;

export default function VerificationGateModal({
  user,           // supabase user object
  mode = 'seller', // 'seller' | 'buyer' — default preserves existing behavior
  cardKey,        // (seller mode) collections row key to unpause on success
  priceUsd,       // listing price for display (both modes)
  onClose,        // user dismissed without verifying
  onPublished,    // (seller mode) verification succeeded AND is_paused flipped
  onVerified,     // (both modes) verification confirmed; buyer-mode side-effect hook
}) {
  const isBuyerMode = mode === 'buyer';
  const [stage, setStage] = useState('intro'); // 'intro' | 'verifying' | 'success' | 'submitted' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const mountedRef = useRef(true);
  const personaClientRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ESC dismisses the modal at any view
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && stage !== 'verifying') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, stage]);

  // ─── Flip is_paused = false once verification confirms server-side ───
  const unpauseListing = useCallback(async () => {
    if (!cardKey || !user?.id) return false;
    const { error } = await supabase
      .from('collections')
      .update({ is_paused: false })
      .eq('user_id', user.id)
      .eq('card_key', cardKey);
    if (error) {
      console.error('[VerificationGateModal] unpause failed:', error.message);
      return false;
    }
    return true;
  }, [cardKey, user?.id]);

  const pollForVerification = useCallback(async () => {
    const start = Date.now();
    while (Date.now() - start < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const { data } = await supabase
        .from('profiles')
        .select('identity_verified')
        .eq('id', user.id)
        .maybeSingle();
      if (data?.identity_verified) {
        // Mode-specific side-effects.
        // - Seller: unpause the listing the gate paused. Failure flips to error.
        // - Buyer: no listing to touch; verification alone is the unlock.
        let ok = true;
        if (!isBuyerMode) {
          ok = await unpauseListing();
        }
        if (mountedRef.current) {
          setStage(ok ? 'success' : 'error');
          if (!ok) setErrorMsg("Verification succeeded but publishing your listing didn't. Email keeper18@collectionkeeper.org.");
        }
        if (ok && mountedRef.current) {
          // Brief success pause so the user sees the confirmation chip before
          // the parent dismisses + re-routes them (buyer: back to startEscrow;
          // seller: listing publishes).
          setTimeout(() => {
            if (!mountedRef.current) return;
            if (onVerified) onVerified();
            if (!isBuyerMode && onPublished) onPublished();
          }, 1400);
        }
        return;
      }
    }
    // Timeout — webhook may still fire; show a softer outcome message.
    if (mountedRef.current) setStage('submitted');
  }, [user?.id, unpauseListing, onPublished, onVerified, isBuyerMode]);

  const launchPersonaFlow = useCallback(async () => {
    if (!PERSONA_TEMPLATE_ID) {
      setErrorMsg('Identity verification is not configured for this environment. Email keeper18@collectionkeeper.org.');
      setStage('error');
      return;
    }
    setErrorMsg('');
    setStage('verifying');

    // Pre-fill name fields when available — stronger ID match + lower friction.
    const { data: profile } = await supabase
      .from('profiles')
      .select('legal_first_name, legal_last_name')
      .eq('id', user.id)
      .maybeSingle();
    const prefillFields = {};
    if (profile?.legal_first_name) prefillFields.nameFirst = profile.legal_first_name;
    if (profile?.legal_last_name) prefillFields.nameLast = profile.legal_last_name;

    const client = new Persona.Client({
      templateId: PERSONA_TEMPLATE_ID,
      ...(PERSONA_ENVIRONMENT_ID ? { environmentId: PERSONA_ENVIRONMENT_ID } : {}),
      referenceId: user.id,
      ...(Object.keys(prefillFields).length ? { fields: prefillFields } : {}),
      onReady: () => client.open(),
      onComplete: () => { pollForVerification(); },
      onCancel: () => {
        if (mountedRef.current) setStage('intro');
      },
      onError: (err) => {
        console.error('[Persona] gate-flow error:', err);
        if (mountedRef.current) {
          setErrorMsg('Verification ran into a problem. Try again, or email keeper18@collectionkeeper.org.');
          setStage('error');
        }
      },
    });
    personaClientRef.current = client;
  }, [user?.id, pollForVerification]);

  const priceStr = typeof priceUsd === 'number'
    ? `$${priceUsd.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
    : null;

  // Mode-derived copy. Seller mode reproduces the original Phase 2E.1 strings
  // verbatim; buyer mode reframes for the buy-side flow (no draft to save,
  // verification returns user to the Open Escrow CTA).
  const COPY = isBuyerMode ? {
    explanation: priceStr
      ? (<>This listing is priced at <strong className="vgm-price">{priceStr}</strong>. Escrow listings require ID verification for both parties — yours and the seller's.</>)
      : (<>Escrow listings require ID verification for both parties — yours and the seller's.</>),
    reassurance: "✓ No charge yet — you'll see the Escrow.com checkout right after you verify.",
    step3: 'You return to the listing to fund escrow',
    secondaryLabel: 'Back to listing',
    secondaryAriaClose: 'Close — back to listing',
    successText: "You're verified. Returning you to the listing to fund the deal.",
    submittedBody: "You'll be able to fund escrow once verification confirms — usually within a minute. You can close this window.",
  } : {
    explanation: priceStr
      ? (<>Your listing is priced at <strong className="vgm-price">{priceStr}</strong>. High-value listings go through Escrow.com and require ID verification for both parties.</>)
      : (<>This listing requires ID verification to publish. High-value listings go through Escrow.com and require ID verification for both parties.</>),
    reassurance: "✓ Your draft is saved. Verify now, and we'll publish right after. Or come back anytime — it'll be waiting.",
    step3: 'Your listing publishes immediately on success',
    secondaryLabel: 'Save draft for later',
    secondaryAriaClose: 'Close — save draft for later',
    successText: priceStr ? `Your ${priceStr} listing is publishing now.` : 'Your listing is publishing now.',
    submittedBody: 'Persona is still processing. Your listing will publish automatically once verification confirms — usually within a minute. You can close this window.',
  };

  return (
    <>
      <style>{GATE_CSS}</style>
      <div className="vgm-overlay" onClick={stage === 'verifying' ? undefined : onClose}>
        <div
          className="vgm-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Verification required to publish high-value listing"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Amber header — matches the escrow-protected visual language */}
          <div className="vgm-header">
            <div className="vgm-header-inner">
              <span className="vgm-shield" aria-hidden="true">🛡</span>
              <span className="vgm-header-text">Verification required</span>
            </div>
            {stage !== 'verifying' && (
              <button
                type="button"
                onClick={onClose}
                aria-label={COPY.secondaryAriaClose}
                className="vgm-close"
              >×</button>
            )}
          </div>

          <div className="vgm-body">
            {stage === 'intro' && (
              <>
                <div className="vgm-explanation">
                  {COPY.explanation}
                </div>

                <div className="vgm-reassurance">
                  {COPY.reassurance}
                </div>

                <div className="vgm-steps-label">// HOW IT WORKS //</div>
                <ol className="vgm-steps">
                  <li><span className="vgm-step-num">1</span> Take a photo of your government ID</li>
                  <li><span className="vgm-step-num">2</span> Take a selfie to match</li>
                  <li><span className="vgm-step-num">3</span> {COPY.step3}</li>
                </ol>

                <div className="vgm-ctas">
                  <button
                    onClick={launchPersonaFlow}
                    disabled={!PERSONA_TEMPLATE_ID}
                    className="vgm-primary"
                    style={{ opacity: PERSONA_TEMPLATE_ID ? 1 : 0.5, cursor: PERSONA_TEMPLATE_ID ? 'pointer' : 'not-allowed' }}
                  >
                    Verify now (~3 min)
                  </button>
                  <button onClick={onClose} className="vgm-secondary">
                    {COPY.secondaryLabel}
                  </button>
                </div>

                {!PERSONA_TEMPLATE_ID && (
                  <div className="vgm-error-note">
                    Identity verification isn't configured in this environment yet.
                  </div>
                )}

                <div className="vgm-footnote">
                  CK never sees your ID. Verified by Persona.
                </div>
              </>
            )}

            {stage === 'verifying' && (
              <div className="vgm-inflight">
                <span className="vgm-spinner" aria-hidden="true">◐</span>
                <div className="vgm-inflight-text">
                  Verifying — keep this page open. Usually a few seconds.
                </div>
                <div className="vgm-footnote" style={{ marginTop: 12 }}>
                  Don't close this window — your listing will publish automatically when Persona confirms.
                </div>
              </div>
            )}

            {stage === 'success' && (
              <div className="vgm-success">
                <div className="vgm-success-pill">✓ VERIFIED</div>
                <div className="vgm-success-text">
                  {COPY.successText}
                </div>
              </div>
            )}

            {stage === 'submitted' && (
              <>
                <div className="vgm-submitted">
                  <div className="vgm-submitted-header">Verification submitted</div>
                  <div className="vgm-submitted-body">
                    {COPY.submittedBody}
                  </div>
                </div>
                <div className="vgm-ctas">
                  <button onClick={onClose} className="vgm-secondary">Close</button>
                </div>
              </>
            )}

            {stage === 'error' && (
              <>
                <div className="vgm-error">
                  <div className="vgm-error-header">⚠ Something went wrong</div>
                  <div className="vgm-error-body">{errorMsg || 'Verification ran into a problem. Try again, or email keeper18@collectionkeeper.org.'}</div>
                </div>
                <div className="vgm-ctas">
                  <button onClick={() => setStage('intro')} className="vgm-primary">Try again</button>
                  <button onClick={onClose} className="vgm-secondary">{COPY.secondaryLabel}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────
const GATE_CSS = `
  .vgm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2100; /* above ListingDetailModal at 2000 */
    padding: 20px;
    animation: vgm-fade 0.18s ease-out;
  }
  .vgm-panel {
    background: var(--panel-2);
    border: 0.5px solid rgba(var(--amber-rgb),0.5);
    border-radius: 12px;
    max-width: 460px;
    width: 100%;
    max-height: 90vh;
    overflow: auto;
    position: relative;
    animation: vgm-slide 0.22s ease-out;
    color: var(--text-bright);
    font-family: 'Rajdhani', sans-serif;
  }
  .vgm-header {
    position: sticky;
    top: 0;
    background: rgba(var(--amber-rgb),0.08);
    border-bottom: 0.5px solid rgba(var(--amber-rgb),0.4);
    padding: 14px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    z-index: 2;
  }
  .vgm-header-inner { display: flex; align-items: center; gap: 8px; }
  .vgm-shield { font-size: 18px; }
  .vgm-header-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    color: var(--warn);
    letter-spacing: 0.02em;
  }
  .vgm-close {
    background: transparent;
    border: none;
    font-size: 22px;
    color: var(--muted-5);
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .vgm-close:hover { color: var(--text); }

  .vgm-body { padding: 18px; }

  .vgm-explanation {
    font-size: 14px;
    color: var(--text);
    line-height: 1.55;
    margin-bottom: 14px;
  }
  .vgm-price {
    color: var(--warn);
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
  }

  .vgm-reassurance {
    background: rgba(var(--ok-2-rgb),0.08);
    border: 0.5px solid rgba(var(--ok-2-rgb),0.45);
    border-radius: 4px;
    padding: 12px 14px;
    font-size: 13px;
    color: var(--text);
    line-height: 1.5;
    margin-bottom: 16px;
  }

  .vgm-steps-label {
    font-family: 'Share Tech Mono', monospace;
    font-size: 11px;
    letter-spacing: 3px;
    color: var(--muted-5);
    margin-bottom: 10px;
  }
  .vgm-steps {
    list-style: none;
    padding: 0;
    margin: 0 0 18px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .vgm-steps li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: var(--text);
    padding: 8px 10px;
    background: rgba(var(--wash-rgb),0.03);
    border-radius: 3px;
    border-left: 2px solid rgba(var(--amber-rgb),0.5);
  }
  .vgm-step-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    color: var(--warn);
    background: rgba(var(--amber-rgb),0.15);
    border-radius: 50%;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .vgm-ctas {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .vgm-primary {
    background: rgba(var(--purple-rgb),0.18);
    color: #fff;
    border: 1px solid rgba(var(--purple-rgb),0.6);
    padding: 13px 16px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.02em;
    min-height: 44px;
    width: 100%;
  }
  .vgm-primary:hover:not(:disabled) { background: rgba(var(--purple-rgb),0.28); }
  .vgm-secondary {
    background: transparent;
    color: var(--violet);
    border: 0.5px solid rgba(var(--violet-rgb),0.4);
    padding: 11px 16px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    min-height: 44px;
    width: 100%;
  }
  .vgm-secondary:hover { border-color: rgba(var(--violet-rgb),0.7); }

  .vgm-footnote {
    font-size: 11px;
    color: var(--muted-6);
    text-align: center;
    margin-top: 14px;
    line-height: 1.5;
  }
  .vgm-error-note {
    font-family: 'Share Tech Mono', monospace;
    font-size: 11px;
    color: var(--err);
    margin-top: 10px;
    text-align: center;
  }

  /* ─── In-flight state ─── */
  .vgm-inflight {
    text-align: center;
    padding: 24px 12px;
  }
  .vgm-spinner {
    font-family: 'JetBrains Mono', monospace;
    font-size: 28px;
    color: var(--cyan);
    display: inline-block;
    animation: vgm-spin 1.2s linear infinite;
  }
  .vgm-inflight-text {
    font-size: 14px;
    color: var(--text);
    margin-top: 14px;
  }

  /* ─── Success state ─── */
  .vgm-success {
    text-align: center;
    padding: 24px 12px;
    background: rgba(var(--ok-2-rgb),0.08);
    border: 0.5px solid rgba(var(--ok-2-rgb),0.45);
    border-radius: 4px;
  }
  .vgm-success-pill {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
    padding: 4px 10px;
    background: rgba(var(--ok-2-rgb),0.22);
    color: var(--ok);
    border: 0.5px solid rgba(var(--ok-2-rgb),0.45);
    border-radius: 3px;
    margin-bottom: 12px;
  }
  .vgm-success-text {
    font-size: 14px;
    color: var(--text);
    line-height: 1.5;
  }

  /* ─── Submitted (timeout) state ─── */
  .vgm-submitted {
    padding: 16px;
    background: rgba(var(--cyan-rgb),0.06);
    border: 0.5px solid rgba(var(--cyan-rgb),0.35);
    border-radius: 4px;
    margin-bottom: 14px;
  }
  .vgm-submitted-header {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    color: var(--card-title-cyan);
    margin-bottom: 6px;
    letter-spacing: 0.02em;
  }
  .vgm-submitted-body {
    font-size: 13px;
    color: var(--text);
    line-height: 1.55;
  }

  /* ─── Error state ─── */
  .vgm-error {
    padding: 16px;
    background: rgba(var(--err-rgb),0.10);
    border: 0.5px solid rgba(var(--err-rgb),0.45);
    border-radius: 4px;
    margin-bottom: 14px;
  }
  .vgm-error-header {
    font-family: 'Orbitron', monospace;
    font-size: 12px;
    letter-spacing: 1.5px;
    color: var(--err);
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .vgm-error-body {
    font-size: 13px;
    color: var(--text);
    line-height: 1.55;
  }

  @keyframes vgm-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes vgm-slide {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes vgm-spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 600px) {
    .vgm-overlay { padding: 0; align-items: stretch; }
    .vgm-panel {
      max-width: 100%;
      max-height: 100vh;
      height: 100vh;
      border-radius: 0;
      border: none;
    }
    /* Full-screen takeover: the sticky header hits the physical top of the
       screen, so it must carry the native status-bar inset itself. */
    .vgm-header {
      padding-top: calc(14px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)));
    }
  }
`;
